import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { lookup, nameOf, type NumberInfo } from '@stitch/pbx-adapter/describe';
import type { Directory, DirectoryEntry } from '../api/client';
import { Icon } from '../lib/icons';

/** Something a picker can choose: a person, a voicemail box, a feature, or a typed number. */
interface Option {
  value: string;
  name: string;
  sub: string;
  group: string;
}

const GROUPS: { kind: DirectoryEntry['kind']; title: string; sub: string }[] = [
  { kind: 'person', title: 'People', sub: 'Ext' },
  { kind: 'autoAttendant', title: 'Auto attendants', sub: 'Menu' },
  { kind: 'ringGroup', title: 'Ring groups', sub: 'Ring group' },
  { kind: 'callQueue', title: 'Call queues', sub: 'Queue' },
  { kind: 'timeRule', title: 'Time of day rules', sub: 'Rule' },
  { kind: 'callPark', title: 'Park orbits', sub: 'Orbit' },
  { kind: 'other', title: 'Other', sub: '' }
];

export type PickerMode =
  /** Anywhere a call can be sent: people, voicemail, menus, groups, queues, other, or a phone number. */
  | 'destination'
  /** People and phone numbers (ring group stages). */
  | 'members'
  /** People only (queue agents). */
  | 'people'
  /** Anything in the company a phone number can point at: no park orbits, no typed numbers. */
  | 'account';

function optionsFor(dir: Directory | undefined, mode: PickerMode): Option[] {
  const entries = dir?.entries ?? [];
  const out: Option[] = [];
  const isBox = (e: DirectoryEntry) => e.kind === 'person' && e.featureSet === 'mailbox';
  const account = mode === 'account';
  for (const g of GROUPS) {
    if (mode !== 'destination' && !account && g.kind !== 'person') continue;
    // Time of day rules are for numbers to point at; park orbits never are.
    if ((g.kind === 'timeRule' && !account) || (g.kind === 'callPark' && account)) continue;
    for (const e of entries.filter((x) => x.kind === g.kind && !isBox(x))) {
      out.push({ value: e.extension, name: lookup(dir, e.extension).name, sub: g.kind === 'other' ? `${e.label ?? ''} ${e.extension}`.trim() : `${g.sub} ${e.extension}`, group: g.title });
    }
    // Voicemail boxes are mailboxes, not people: they take calls (and messages), but never ring a phone.
    if (g.kind === 'person' && (mode === 'destination' || account)) {
      for (const e of entries.filter(isBox)) out.push({ value: e.extension, name: lookup(dir, e.extension).name, sub: `Voicemail box ${e.extension}`, group: 'Voicemail boxes' });
    }
    if (g.kind === 'person' && mode === 'destination' && dir?.mailboxPrefix) {
      for (const e of entries.filter((x) => x.kind === 'person' && !isBox(x))) {
        const v = `${dir.mailboxPrefix}${e.extension}`;
        out.push({ value: v, name: lookup(dir, v).name, sub: `Voicemail ${v}`, group: 'Voicemail' });
      }
    }
  }
  // Voicemail reads better right after People.
  const order = ['People', 'Voicemail boxes', 'Voicemail', ...GROUPS.slice(1).map((g) => g.title)];
  return out.sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group));
}

/** "Ext 201", "Voicemail 8201", "Phone number": the small grey line under a name. */
export function subLine(info: NumberInfo): string {
  switch (info.kind) {
    case 'person':
      return `Ext ${info.number}`;
    case 'voicemail':
      return `Voicemail ${info.number}`;
    case 'mailbox':
      return `Voicemail box ${info.number}`;
    case 'external':
      return 'Phone number';
    case 'unknown':
      return 'Not in your account';
    default:
      return info.number;
  }
}

const DIALLABLE = /^\+?[0-9*#]{2,32}$/;

/** The search-and-pick panel shared by every picker. Opens inline, so it works in dialogs and on phones. */
function NumberSearch({
  options,
  exclude,
  allowTyped,
  onPick,
  onClose,
  label
}: {
  options: Option[];
  exclude?: readonly string[];
  allowTyped: boolean;
  onPick: (value: string) => void;
  onClose: () => void;
  label: string;
}) {
  const [q, setQ] = useState('');
  const panel = useRef<HTMLDivElement>(null);
  const needle = q.trim().toLowerCase();
  const shown = options.filter((o) => !exclude?.includes(o.value) && (!needle || o.name.toLowerCase().includes(needle) || o.value.includes(needle) || o.sub.toLowerCase().includes(needle)));
  const typed = q.trim().replace(/[\s().-]/g, '');
  const offerTyped = allowTyped && DIALLABLE.test(typed) && !options.some((o) => o.value === typed);
  const groups = [...new Set(shown.map((o) => o.group))];

  const move = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Close only the picker, not the dialog around it.
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      onClose();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(panel.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [])];
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? (i < 0 ? 0 : Math.min(i + 1, items.length - 1)) : i <= 0 ? -1 : i - 1;
    if (next < 0) panel.current?.querySelector<HTMLInputElement>('input')?.focus();
    else items[next]?.focus();
  };

  return (
    <div className="picker-panel" ref={panel} onKeyDown={move}>
      <input
        className="input"
        type="search"
        aria-label={`Search: ${label}`}
        placeholder={allowTyped ? 'Search by name, or type a phone number' : 'Search by name or extension'}
        value={q}
        autoFocus
        autoComplete="off"
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (offerTyped) onPick(typed);
          else if (shown[0]) onPick(shown[0].value);
        }}
      />
      <div role="listbox" aria-label={label} className="picker-list">
        {offerTyped && (
          <button type="button" role="option" aria-selected={false} className="picker-option" onClick={() => onPick(typed)}>
            <span className="picker-name">Use the number {typed}</span>
            <span className="picker-sub">{typed.length >= 7 || typed.startsWith('+') ? 'Phone number' : 'Number'}</span>
          </button>
        )}
        {groups.map((g) => (
          <div key={g} className="picker-group" role="group" aria-label={g}>
            <span className="picker-group-title">{g}</span>
            {shown
              .filter((o) => o.group === g)
              .map((o) => (
                <button key={o.value} type="button" role="option" aria-selected={false} className="picker-option" onClick={() => onPick(o.value)}>
                  <span className="picker-name">{o.name}</span>
                  <span className="picker-sub">{o.sub}</span>
                </button>
              ))}
          </div>
        ))}
        {!shown.length && !offerTyped && <span className="picker-empty">{allowTyped ? 'Nothing matches. Type a phone number to use it.' : 'Nothing matches.'}</span>}
      </div>
    </div>
  );
}

/** One place to send calls, chosen by name. */
export function DestinationPicker({
  label,
  value,
  onChange,
  dir,
  help,
  error,
  mode = 'destination',
  hideLabel,
  clearable = true
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  dir: Directory | undefined;
  help?: string;
  error?: string;
  mode?: PickerMode;
  /** Keep the label for screen readers only (rows that already say what this is). */
  hideLabel?: boolean;
  /** Offer a clear button (rows with their own remove button don't need one). */
  clearable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const options = useMemo(() => optionsFor(dir, mode), [dir, mode]);
  const info = lookup(dir, value);
  return (
    <div className="field">
      <span className={hideLabel ? 'sr-only' : 'field-label'} id={`${id}-label`}>
        {label}
      </span>
      <div className="picker-row">
        <button
          type="button"
          className={`picker-trigger${error ? ' invalid' : ''}`}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-value`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="picker-value" id={`${id}-value`}>
            {value ? (
              <>
                <span className="picker-name">{info.name}</span>
                <span className="picker-sub">{subLine(info)}</span>
              </>
            ) : (
              <span className="muted">Choose…</span>
            )}
          </span>
          <Icon name="chev" size={16} />
        </button>
        {value && clearable && (
          <button type="button" className="icon-btn" aria-label={`Clear: ${label}`} title="Clear" onClick={() => onChange('')}>
            <Icon name="x" size={15} />
          </button>
        )}
      </div>
      {open && (
        <NumberSearch
          label={label}
          options={options}
          allowTyped={mode !== 'people' && mode !== 'account'}
          onClose={() => setOpen(false)}
          onPick={(v) => {
            onChange(v);
            setOpen(false);
          }}
        />
      )}
      {help && <span className="hint">{help}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

/** Several people (or numbers), shown as names you can remove. Value: numbers separated by spaces. */
export function MemberPicker({
  label,
  value,
  onChange,
  dir,
  help,
  error,
  mode = 'members',
  addLabel
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  dir: Directory | undefined;
  help?: string;
  error?: string;
  mode?: PickerMode;
  addLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const members = value.split(/[\s,;]+/).filter(Boolean);
  const options = useMemo(() => optionsFor(dir, mode), [dir, mode]);
  return (
    <div className="field">
      <span className="field-label" id={`${id}-label`}>
        {label}
      </span>
      {members.length > 0 && (
        <ul className="member-chips" aria-labelledby={`${id}-label`}>
          {members.map((m) => {
            const info = lookup(dir, m);
            return (
              <li key={m} className={`member-chip${info.kind === 'unknown' && dir ? ' unknown' : ''}`}>
                <span className="picker-name">{info.name}</span>
                <span className="picker-sub">{info.kind === 'person' ? m : subLine(info)}</span>
                <button type="button" aria-label={`Remove ${nameOf(dir, m)}`} onClick={() => onChange(members.filter((x) => x !== m).join(' '))}>
                  <Icon name="x" size={13} strokeWidth={2.4} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {open ? (
        <NumberSearch
          label={label}
          options={options}
          exclude={members}
          allowTyped={mode !== 'people'}
          onClose={() => setOpen(false)}
          onPick={(v) => {
            if (!members.includes(v)) onChange([...members, v].join(' '));
            setOpen(false);
          }}
        />
      ) : (
        <button type="button" className="add-member" aria-label={`${addLabel}: ${label}`} onClick={() => setOpen(true)}>
          <Icon name="plus" size={15} strokeWidth={2.2} />
          {addLabel}
        </button>
      )}
      {help && <span className="hint">{help}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}
